import { useEffect, useRef, useState } from 'react';

import { EMAIL_RESEND_COOLDOWN_SECONDS, isMfaSessionEnded } from '../../../../shared/MfaManager/consts';
import { useAddToast } from '../../../contexts/ToastContext';
import { getErrorMessage } from '../../../ReactQuery';
import { hardRedirectTo } from '../../../utils';
import Button from '../../generic/Button';
import AlphanumericCodeInput, { type AlphanumericCodeInputHandle } from '../../generic/Forms/AlphanumericCodeInput';
import VerificationCodeInput, { type VerificationCodeInputHandle } from '../../generic/Forms/VerificationCodeInput';
import UseDifferentAccountButton from '../components/UseDifferentAccountButton';
import { useResendEmailMfa } from '../hooks/useResendEmailMfa';
import { useVerifyMfa } from '../hooks/useVerifyMfa';
import type { MfaMethodType } from '../../../../shared/model-typings/mfaMethod';

import styles from '../Login.module.less';

export type MfaStepProps = {
  mfaMethod: MfaMethodType;
  /**
   * Post-login redirect target, propagated from the URL `?next=` so a redirect-initiated login still lands the user
   *  back at their original destination once MFA completes.
   */
  next?: string;
  /** Returns to the identifier step — for the manual "use a different account" link and when the MFA session dies. */
  onBack: () => void;
};

/**
 * The MFA challenge: after a credential/social login lands in the partially-authenticated state, the user enters the
 * code from their authenticator app (TOTP) or email to finish signing in. TOTP offers a backup-code fallback; email
 * offers a resend. On success the full session is established server-side and we hard-redirect into the app.
 */
const MfaStep = ({ mfaMethod, next, onBack }: MfaStepProps) => {
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const verifyMfa = useVerifyMfa();
  const resendEmailMfa = useResendEmailMfa();
  const addToast = useAddToast();
  const codeInputRef = useRef<VerificationCodeInputHandle | AlphanumericCodeInputHandle | null>(null);

  // Tick the resend cooldown down to zero, one second at a time.
  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => setResendCooldown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // React to the verification result from an effect rather than the mutate callbacks. React Query runs those callbacks
  // before notifying its subscribers, so navigating away there — on success, or when a dead session forces a restart —
  // unmounts this step while React Query is still about to update it, warning about a state update on an unmounted
  // component under React 17. Reacting to the settled result defers any navigation until after this step re-renders.
  useEffect(() => {
    if (verifyMfa.isSuccess) {
      hardRedirectTo(next ?? '/');
      return;
    }
    if (!verifyMfa.isError) {
      return;
    }
    // The verify endpoint rejects with 401/429 for both wrong codes and dead sessions. A wrong code is shown inline so
    // the user can retry; a dead session can't be retried, so toast why and restart the flow. Anything unexpected
    // (network failure, 5xx) is a toast.
    const requestError = verifyMfa.error;
    if (requestError instanceof Response && requestError.status < 500) {
      const message = getErrorMessage(requestError, 'Invalid code. Please try again.');
      if (isMfaSessionEnded(message)) {
        addToast({ type: 'error', content: message });
        onBack();
        return;
      }
      setCode('');
      setError(message);
      // Send the cursor back to the first box — without this it stays on whichever box the user last typed in.
      codeInputRef.current?.focusFirst();
      return;
    }
    addToast({ type: 'error', content: getErrorMessage(requestError, 'Something went wrong. Please try again.') });
  }, [verifyMfa.isSuccess, verifyMfa.isError, verifyMfa.error, next, onBack, addToast]);

  // Resend errors are surfaced by the hook now; every failure toasts, and a dead pending-MFA session additionally
  // restarts the flow instead of leaving the user stuck on a challenge they can no longer complete.
  useEffect(() => {
    if (!resendEmailMfa.isError) {
      return;
    }
    const message = getErrorMessage(resendEmailMfa.error, 'Could not resend the code. Please try again.');
    addToast({ type: 'error', content: message });
    if (isMfaSessionEnded(message)) {
      onBack();
    }
  }, [resendEmailMfa.isError, resendEmailMfa.error, onBack, addToast]);

  const handleSubmit = (submittedCode: string = code) => {
    if (!submittedCode || verifyMfa.isLoading) {
      return;
    }
    setError(null);
    verifyMfa.mutate({ code: submittedCode, isBackupCode: useBackupCode });
  };

  const handleToggleBackupCode = () => {
    setUseBackupCode((previous) => !previous);
    setCode('');
    setError(null);
  };

  const handleResend = () => {
    if (resendCooldown > 0 || resendEmailMfa.isLoading) {
      return;
    }
    resendEmailMfa.mutate(undefined, {
      onSuccess: () => setResendCooldown(EMAIL_RESEND_COOLDOWN_SECONDS),
    });
  };

  let instructions = 'Enter the 6-digit verification code from your authenticator app.';
  if (mfaMethod === 'email') {
    instructions = 'Enter the 6-digit verification code sent to your email.';
  } else if (useBackupCode) {
    instructions = 'Enter one of your backup codes.';
  }

  return (
    <div className={styles.card}>
      <h1 className={styles.heading}>Verify your identity</h1>
      <p className={styles.placeholder}>{instructions}</p>
      <div className={styles.codeField} data-test-id="mfa-code-field">
        {useBackupCode ? (
          <AlphanumericCodeInput
            ref={codeInputRef}
            value={code}
            onChange={setCode}
            onComplete={handleSubmit}
            disabled={verifyMfa.isLoading}
          />
        ) : (
          <VerificationCodeInput
            ref={codeInputRef}
            value={code}
            onChange={setCode}
            onComplete={handleSubmit}
            disabled={verifyMfa.isLoading}
          />
        )}
      </div>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      <Button isFullWidth size="large" isDisabled={verifyMfa.isLoading} onClick={() => handleSubmit()}>
        Verify
      </Button>
      {mfaMethod === 'email' ? (
        <Button
          type="anchor"
          size="small"
          className={styles.secondaryAction}
          isDisabled={resendCooldown > 0 || resendEmailMfa.isLoading}
          onClick={handleResend}
        >
          {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
        </Button>
      ) : (
        <Button type="anchor" size="small" className={styles.secondaryAction} onClick={handleToggleBackupCode}>
          {useBackupCode ? 'Use authenticator app code' : 'Having trouble? Use a backup code'}
        </Button>
      )}
      <UseDifferentAccountButton onClick={onBack} />
    </div>
  );
};

export default MfaStep;
